import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose, DialogOverlay } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import imgGreenBird from '../../assets/greenbird.png';
import InfiniteScroll from 'react-infinite-scroll-component';
import axios from 'axios';
import { ADD_PLAYLIST, addPlaylist, APPEND_PLAYLIST_TO_PLAYLIST, SET_COVER_GENERATE, SET_PLAYLIST, SET_SELECTED_PLAYLIST } from '@/redux/features/music/playlistSlice';
import { useDispatch, useSelector } from 'react-redux';
import PlaylistLoader from '../playlist/PlaylistLoader';
import { SET_SHOW_LOGIN } from '@/redux/features/auth/authSlice';
import { ListPlus, Music, Pencil, Search } from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import "react-lazy-load-image-component/src/effects/blur.css";
import Lottie from 'lottie-react';
import musicAni from "../../assets/guitar.json";
import { debounce } from '@/utils/debounce';

const PlaylistsComponent = () => {
    const dispatch = useDispatch();
    const { playlists } = useSelector((state) => state.playlists);
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [publish, setPublish] = useState(false);
    const { song } = useSelector(state => state.music);
    const { user } = useSelector(state => state.auth);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [term, setTerm] = useState("");
    const [searchPlaylists, setSearchPlaylists] = useState([]);
    const [noResultMsg, setNoResultMsg] = useState(false);

    // Fetch playlists
    useEffect(() => {
        const fetchPlaylists = async () => {
            try {
                setIsLoading(true);
                if (page === 1) {
                    dispatch(SET_PLAYLIST([]));
                }
                const token = localStorage.getItem('token');
                const res = await axios.get(`/playlists2?page=${page}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                dispatch(APPEND_PLAYLIST_TO_PLAYLIST(res.data.playlists));
                setIsLoading(false);
                if (res.data.playlists.length === 0 || res.data.playlists.length < 10) {
                    setHasMore(false); // No more songs to load
                }
            } catch (error) {
                console.error("Error setting playlists:", error);
                setIsLoading(false);
            }
        };
        fetchPlaylists();
    }, [dispatch, page]);

    // Search playlists with debounce
    useEffect(() => {
        const token = localStorage.getItem('token');
        const debouncedSearch = debounce(async () => {
            if (!term) return;
            try {
                setIsLoading(true);
                const response = await axios.get(`/search/playlists?search=${term}&page=1&limit=20`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSearchPlaylists(response.data.playlists || []);
                setIsLoading(false);
                if ((response.data.playlists || []).length === 0) {
                    setNoResultMsg(true);
                } else {
                    setNoResultMsg(false);
                }
            } catch (error) {
                setIsLoading(false);
                console.error('Search failed:', error);
            }
        }, 500);
        debouncedSearch();
        return () => {
            debouncedSearch.cancel();
        };
    }, [term]);

    // Add new playlist
    const handleAddPlaylist = useCallback(async () => {
        if (newPlaylistName.trim() === '') {
            toast.error("Playlist name cannot be empty");
            return;
        }
        try {
            setIsAdding(true);
            toast.loading("Creating playlist...", { id: "add-playlist-loading" });
            const newPlaylist = await dispatch(addPlaylist({
                playlistName: newPlaylistName,
                publish
            })).unwrap();
            if (newPlaylist) {
                dispatch(ADD_PLAYLIST(newPlaylist));
            }
            // Reset states
            setNewPlaylistName('');
            setShowDialog(false);
            setPublish(false);
            toast.success("Playlist created successfully", { id: "add-playlist-loading" });
            toast.dismiss("add-playlist-loading");
        } catch (error) {
            // toast.error(`Failed to create playlist: ${error.response?.data?.message || error.message}`);
        } finally {
            setIsAdding(false);
        }
    }, [newPlaylistName, publish, dispatch]);

    // Regenerate playlist cover
    const handleRegenerate = async (e, playlist) => {
        e.preventDefault();
        dispatch(SET_SELECTED_PLAYLIST(playlist));
        dispatch(SET_COVER_GENERATE(true));
    };

    // Render playlist card
    const renderPlaylistCard = (playlist, index) => (
        <Link to={`/playlist?id=${playlist?.playlist_slug}`} key={index} className="relative block group">
            {/* Stacked cards container */}
            <div className="relative w-full aspect-[16/9] group">
                {/* Bottom stacked cards */}
                <div className="absolute left-[7%] -top-[3%] w-[86%] h-[92%] bg-green-200 border border-black dark:bg-slate-700 rounded-lg transform -translate-y-[8%] shadow-md group-hover:translate-y-0 transition-transform duration-300" />
                <div className="absolute left-[3.5%] -top-[2%] w-[93%] h-[96%] bg-green-200 border border-black dark:bg-slate-700/95 rounded-lg transform -translate-y-[4%] shadow-lg group-hover:translate-y-0 transition-transform duration-300" />
                {/* Main card */}
                <div className="relative w-full h-full overflow-hidden bg-slate-900 shadow-xl rounded-t-lg">
                    <LazyLoadImage effect="blur" src={playlist.cover_image || imgGreenBird} alt={playlist.name} className="w-full h-full object-cover" loading="lazy" />
                    {/* Dark overlay */}
                    <div className="absolute inset-0 bg-black/10" />
                    <div className="absolute bottom-0.5 right-0.5 bg-black/85 backdrop-blur-sm px-2.5 py-1 rounded-sm flex items-center gap-1 text-white z-20">
                        <span className="font-normal text-xs">
                            {playlist?.song_count || 0}
                        </span>
                        <div className="flex flex-col gap-[2px]">
                            <Music className="w-3 h-3 text-white" />
                        </div>
                    </div>
                    <button onClick={(e) => { e.preventDefault(); handleRegenerate(e, playlist); }} className={`absolute top-0.5 right-0.5 z-20 bg-black/50 backdrop-blur-sm p-1 rounded-sm transition-opacity duration-200 sm:opacity-0 sm:group-hover:opacity-100 hover:bg-black/70 ${window.innerWidth <= 640 ? 'opacity-100' : ''}`}>
                        <Pencil className="w-4 h-4 text-white" />
                    </button>
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                </div>
            </div>
            {/* Title section - Now styled as a connected card */}
            <div className="relative -mt-[1px]">
                <div className="bg-slate-950 rounded-b-lg shadow-lg shadow-green-700/20 border-t border-slate-700/20 py-1.5 px-3">
                    <h3 className="text-white font-medium text-sm sm:text-base truncate group-hover:text-green-400 dark:group-hover:text-pink-400 transition-colors duration-200 relative z-10">
                        {playlist?.name || 'Hello'}
                    </h3>
                    <div className="absolute inset-0 bg-gradient-to-t from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-b-lg" />
                </div>
            </div>
        </Link>
    );

    return (
        <div className='overflow-hidden'>
            <div className={`flex flex-row items-center gap-3 sm:gap-8 justify-between mb-4`}>
                {/* Search input */}
                <div className=" lg:max-w-lg w-full">
                    <div className="flex items-center border border-slate-700 dark:border-pink-700 rounded-sm bg-transparent">
                        <input disabled={playlists?.length === 0} type="text" placeholder="Search playlists" value={term} onChange={(e) => setTerm(e.target.value)} className="w-full placeholder:truncate bg-transparent sm:p-2 p-1.5 text-white dark:text-black focus:outline-none disabled:opacity-50" />
                        <div className="px-2 text-slate-400 dark:text-pink-800">
                            <Search size={18} />
                        </div>
                    </div>
                </div>
                {/* Add new playlist dialog */}
                <Dialog open={showDialog} onOpenChange={setShowDialog}>
                    {!user?.is_guest ? <DialogTrigger asChild>
                        <button className="text-green-400 hover:text-green-300 dark:text-black dark:hover:text-pink-600 transition-colors">
                            <ListPlus size={40} />
                        </button>
                    </DialogTrigger> : <button onClick={() => {
                        toast.error("Login to create playlist", { id: "guest" })
                        dispatch(SET_SHOW_LOGIN(true))
                    }} className="text-green-400 hover:text-green-300 transition-colors">
                        <ListPlus size={40} />
                    </button>}
                    <DialogOverlay className="z-[99999]">
                        <DialogContent className="z-[999999] w-full max-w-[95%] p-4 sm:max-w-sm bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 dark:bg-gradient-to-r dark:from-pink-300 dark:via-pink-400 dark:to-pink-500 text-slate-300 dark:text-pink-950 border-green-400/30 rounded-lg dark:border-pink-900 outline-1">
                            <DialogHeader className='flex items-start'>
                                <DialogTitle>Add New Playlist</DialogTitle>
                                <DialogDescription className="text-slate-400 dark:text-pink-900 dark:font-semibold text-xs sm:text-md">
                                    Fill in the details for your new playlist.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-2.5">
                                <input type="text" placeholder="Playlist Name" value={newPlaylistName} onChange={(e) => setNewPlaylistName(e.target.value)} className="w-full px-2 py-2 rounded bg-slate-800 dark:bg-pink-300 text-sm text-slate-200 dark:text-slate-900 placeholder-slate-400/80 dark:placeholder-slate-900 focus:outline-none focus:ring-1 focus:ring-green-400/30 dark:focus:ring-pink-400 focus:border-transparent" />
                                <label className="switch flex items-center justify-between border-b pb-1 border-slate-700 dark:border-pink-700 cursor-pointer ">
                                    <p className="font-semibold text-sm">Publish</p>
                                    <input checked={publish} onChange={(e) => setPublish(e.target.checked)} type="checkbox" />
                                    <span className="slider dark:bg-pink-200 "></span>
                                </label>
                            </div>
                            <DialogFooter>
                                <div className='flex flex-row justify-end gap-2'>
                                    <Button onClick={handleAddPlaylist} disabled={isAdding} className="bg-green-400 px-8 h-8 sm:h-10 hover:bg-green-300 text-slate-800  dark:bg-pink-700 dark:hover:bg-pink-700/70 dark:text-white dark:hover:text-white dark:border-pink-800">
                                        {isAdding ? 'Creating...' : 'Save'}
                                    </Button>
                                    <DialogClose asChild>
                                        <Button className="bg-slate-700 px-6 h-8 sm:h-10 hover:bg-slate-800 text-slate-300 dark:bg-pink-300 dark:hover:bg-pink-300/70 dark:text-slate-900 dark:hover:text-slate-900 dark:border-pink-800">
                                            Cancel
                                        </Button>
                                    </DialogClose>
                                </div>
                            </DialogFooter>
                        </DialogContent>
                    </DialogOverlay>
                </Dialog>
            </div>

            {/* Main content based on search state */}
            {!term && playlists?.length > 0 ? (
                <InfiniteScroll
                    dataLength={playlists.length}
                    next={() => setPage(prev => prev + 1)}
                    hasMore={hasMore}
                    loader={<Loader />}
                    style={{ overflow: 'visible' }}
                    className={`grid w-full gap-x-4 gap-y-8 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 3xl:grid-cols-7 py-4 sm:py-6 sm:mt-4 mt-2 ${song ? "pb-36 md:pb-28" : "pb-24"}`}
                >
                    {playlists.map((playlist, index) => renderPlaylistCard(playlist, index))}
                </InfiniteScroll>
            ) : !term && isLoading ? (
                <div className={`grid w-full gap-x-4 gap-y-8 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 3xl:grid-cols-7 py-4 sm:py-6 sm:mt-4 mt-2 ${song ? "pb-36 md:pb-28" : "pb-24"}`}>
                    <Loader />
                </div>
            ) : term && !isLoading && searchPlaylists.length > 0 ? (
                <div className={`grid w-full gap-x-4 gap-y-8 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 3xl:grid-cols-7 py-4 sm:py-6 sm:mt-4 mt-2 ${song ? "pb-36 md:pb-28" : "pb-24"}`}>
                    {searchPlaylists.map((playlist, index) => renderPlaylistCard(playlist, index))}
                </div>
            ) : term && !isLoading && noResultMsg ? (
                <div className="text-center py-8 text-slate-300 dark:text-pink-800">
                    No playlists found matching &quot;{term}&quot;. Try a different search.
                </div>
            ) : term && isLoading ? (
                <div className={`grid w-full gap-x-4 gap-y-8 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 3xl:grid-cols-7 py-4 sm:py-6 sm:mt-4 mt-2 ${song ? "pb-36 md:pb-28" : "pb-24"}`}>
                    <Loader />
                </div>
            ) : playlists?.length === 0 && !isLoading && !term ? (
                <div className='flex flex-col items-center justify-center px-4 sm:pt-20 pt-32'>
                    <div className="sm:-mt-16 mt-2 flex items-center justify-center">
                        <div className="max-w-sm w-full mb-24 relative">
                            <Lottie animationData={musicAni} />
                            <p className="absolute sm:bottom-16 bottom-4 text-center w-full text-slate-300 font-semibold">
                                No playlists found. Start creating your first one!
                            </p>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default PlaylistsComponent;

function Loader() {
    return (
        <>
            <PlaylistLoader />
            <PlaylistLoader />
            <PlaylistLoader />
            <PlaylistLoader />
            <PlaylistLoader />
            <PlaylistLoader />
            <PlaylistLoader />
            <PlaylistLoader />
            <PlaylistLoader />
            <PlaylistLoader />
            <PlaylistLoader />
        </>
    )
}